import type { PlatformUserAvatarUploadErrorMessage } from '@provision/entity-types';
import { ApolloError, gql, useLazyQuery } from '@provision/graphql-client';
import { useCallback } from 'react';
import { uploadFileToUrl } from 'src/graphql/entities/files-upload';
import { useNotifications } from 'src/utils/hooks/use-notifications';

const GET_PLATFORM_USER_AVATAR_UPLOAD_INFO_QUERY = gql`
  query GetPlatformUserAvatarUploadInfo($input: PlatformUserAvatarUploadInfoInput!) {
    getPlatformUserAvatarUploadInfo(input: $input) {
      putURL
      fileKey
    }
  }
`;

interface GetPlatformUserAvatarUploadInfoResult {
  getPlatformUserAvatarUploadInfo: {
    putURL: string;
    fileKey: string;
  };
}

const UPLOAD_ERROR_MESSAGES: { match: string; message: string }[] = [
  {
    match: 'Unsupported content type' satisfies PlatformUserAvatarUploadErrorMessage,
    message: 'This image type is not supported.',
  },
  { match: 'Not authorized', message: 'You do not have access to upload an avatar here.' },
];

const DEFAULT_UPLOAD_ERROR = 'Could not upload the avatar. Please try again.';

function parseAvatarUploadError(error: ApolloError): string {
  return (
    UPLOAD_ERROR_MESSAGES.find((entry) =>
      error.graphQLErrors.some((gqlError) => gqlError.message.includes(entry.match)),
    )?.message ?? DEFAULT_UPLOAD_ERROR
  );
}

export function usePlatformUserAvatarUpload() {
  const { showErrorAlert } = useNotifications();
  const [getUploadInfo] = useLazyQuery<GetPlatformUserAvatarUploadInfoResult>(
    GET_PLATFORM_USER_AVATAR_UPLOAD_INFO_QUERY,
    { fetchPolicy: 'no-cache', context: { ttl: 0, queryDeduplication: false } },
  );

  const uploadAvatar = useCallback(
    async (file: File, partnerId: number): Promise<string | null> => {
      try {
        const { data } = await getUploadInfo({
          variables: { input: { contentType: file.type, partnerId } },
        });

        const uploadInfo = data?.getPlatformUserAvatarUploadInfo;
        if (!uploadInfo) {
          showErrorAlert(DEFAULT_UPLOAD_ERROR);
          return null;
        }

        const putResult = await uploadFileToUrl(file, uploadInfo.putURL);
        if (putResult === null) {
          showErrorAlert(DEFAULT_UPLOAD_ERROR);
          return null;
        }

        return uploadInfo.fileKey;
      } catch (error) {
        showErrorAlert(
          error instanceof ApolloError ? parseAvatarUploadError(error) : DEFAULT_UPLOAD_ERROR,
        );
        return null;
      }
    },
    [getUploadInfo, showErrorAlert],
  );

  return { uploadAvatar };
}
